import { getFirestore } from '../firebase.js';
import { 
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';

const db = getFirestore();
const dnaProfilesCollection = collection(db, 'dnaProfiles');
const dnaInteractionsCollection = collection(db, 'dnaInteractions');

// DNA interaction types
export const DNA_INTERACTIONS = {
  CLUTCH: 'clutch',
  FUNNY: 'funny',
  HYPE: 'hype',
  SKILL: 'skill',
  ENTERTAINING: 'entertaining'
};

/**
 * Creates or updates a DNA profile for a streamer
 * @param {string} userId - The streamer's user ID
 * @param {Object} profileData - The profile data
 * @returns {Promise<Object>} The created/updated profile
 */
export const updateDnaProfile = async (userId, profileData) => {
  try {
    const profileDoc = doc(dnaProfilesCollection, userId);
    await setDoc(profileDoc, {
      ...profileData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return getDnaProfile(userId);
  } catch (error) {
    console.error('Error updating DNA profile:', error);
    throw new Error('Failed to update DNA profile');
  }
};

/**
 * Gets a streamer's DNA profile
 * @param {string} userId - The streamer's user ID
 * @returns {Promise<Object>} The DNA profile
 */
export const getDnaProfile = async (userId) => {
  try {
    const profileDoc = doc(dnaProfilesCollection, userId);
    const profileSnap = await getDoc(profileDoc);
    
    if (!profileSnap.exists()) {
      return {
        userId,
        stats: {
          clutch: 0,
          funny: 0,
          hype: 0,
          skill: 0,
          entertaining: 0
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
    }
    
    return profileSnap.data();
  } catch (error) {
    console.error('Error getting DNA profile:', error);
    throw new Error('Failed to get DNA profile');
  }
};

/**
 * Records a DNA interaction
 * @param {string} streamerId - The streamer's user ID
 * @param {string} viewerId - The viewer's user ID
 * @param {string} interactionType - The type of interaction
 * @returns {Promise<Object>} The updated profile
 */
export const recordDnaInteraction = async (streamerId, viewerId, interactionType) => {
  try {
    if (!DNA_INTERACTIONS[interactionType.toUpperCase()]) {
      throw new Error('Invalid interaction type');
    }
    
    // Check if viewer has already interacted in the last hour
    const oneHourAgo = new Date(Date.now() - 3600000);
    const q = query(
      dnaInteractionsCollection,
      where('streamerId', '==', streamerId),
      where('viewerId', '==', viewerId),
      where('interactionType', '==', interactionType),
      where('timestamp', '>', oneHourAgo)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      throw new Error('You have already interacted with this streamer recently');
    }
    
    // Record the interaction
    const interactionDoc = doc(dnaInteractionsCollection);
    await setDoc(interactionDoc, {
      streamerId,
      viewerId,
      interactionType,
      timestamp: serverTimestamp()
    });
    
    // Update the DNA profile stats
    const profileDoc = doc(dnaProfilesCollection, streamerId);
    await updateDoc(profileDoc, {
      [`stats.${interactionType}`]: increment(1),
      updatedAt: serverTimestamp()
    });
    
    return getDnaProfile(streamerId);
  } catch (error) {
    console.error('Error recording DNA interaction:', error);
    throw new Error('Failed to record DNA interaction');
  }
};

/**
 * Gets the top streamers by DNA interaction type
 * @param {string} interactionType - The type of interaction
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of top streamers
 */
export const getTopStreamersByInteraction = async (interactionType, limit = 10) => {
  try {
    if (!DNA_INTERACTIONS[interactionType.toUpperCase()]) {
      throw new Error('Invalid interaction type');
    }
    
    const q = query(
      dnaProfilesCollection,
      orderBy(`stats.${interactionType}`, 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting top streamers:', error);
    throw new Error('Failed to get top streamers');
  }
};

/**
 * Gets a streamer's interaction history
 * @param {string} streamerId - The streamer's user ID
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of interactions
 */
export const getStreamerInteractionHistory = async (streamerId, limit = 50) => {
  try {
    const q = query(
      dnaInteractionsCollection,
      where('streamerId', '==', streamerId),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting interaction history:', error);
    throw new Error('Failed to get interaction history');
  }
}; 